import { useQuery } from '@tanstack/react-query';
import { useState } from 'react';
import { useNavigate } from 'react-router';
import { Button } from '../../components/ui/Button.js';
import { FormField, Input, Select } from '../../components/ui/FormField.js';
import { useSaveMutation } from '../../hooks/useSaveMutation.js';
import { ApiError, api } from '../../services/api.js';
import { fromMytInput } from '../../utils/myt.js';

/**
 * 421 (#1528, mock-up frame 1): a LATE extension, recorded by staff holding "change past bookings" once the rental's
 * return time has passed (the car still out, or the rental ended). It is quoted on the same car from the old return,
 * then either recorded as already paid outside the app (the amount entered is its price) or left owed by CARii's QR,
 * when the customer is told. The platform decides every refusal; this form only checks that an outside payment has an
 * amount and a reference before asking.
 */
export interface LateContext {
  return_passed: boolean;
  allowed: boolean;
  tail_booking_id: string;
  tail_status: string;
  pickup_at: string;
  car_id: string;
}

interface QuotePreview {
  outcome: string;
  pricing_template_id?: string;
  currency_symbol?: string;
  total_minor?: number;
}

const METHODS: Array<[string, string]> = [
  ['cash', 'Cash'],
  ['bank_transfer', 'Bank transfer'],
  ['qr', 'QR'],
  ['other', 'Other'],
];

const passedLabel = new Intl.DateTimeFormat('en-GB', {
  timeZone: 'Asia/Kuala_Lumpur',
  day: 'numeric',
  month: 'short',
  hour: 'numeric',
  minute: '2-digit',
  hour12: true,
});
const when = (iso: string) => passedLabel.format(new Date(iso)).replace(/\s?(am|pm)$/i, ' $1');
const rm = (minor: number) => `RM ${(minor / 100).toFixed(2)}`;

/** 421 (US1-5): the car's clash named, from the refusal's `clash` (a booking or its extension, or another block). */
function clashSentence(clash: unknown): string | null {
  const c = clash as { source_type?: string; source_id?: string; start_at?: string } | undefined;
  if (!c?.start_at) return null;
  const booked = c.source_type === 'booking' || c.source_type === 'booking_extension';
  const what = booked
    ? `booked from ${when(c.start_at)}${c.source_id ? ` (booking ${c.source_id.slice(-8)})` : ''}`
    : `blocked from ${when(c.start_at)}`;
  return `The car is ${what}. Choose an earlier return time. Nothing was saved.`;
}

export function LateExtensionForm({ late, onCancel }: { late: LateContext; onCancel: () => void }) {
  const navigate = useNavigate();
  const [returnAt, setReturnAt] = useState('');
  const [kind, setKind] = useState<'outside' | 'qr'>('outside');
  const [amount, setAmount] = useState('');
  const [method, setMethod] = useState('cash');
  const [reference, setReference] = useState('');
  const [error, setError] = useState('');

  const returnIso = fromMytInput(returnAt) ?? '';
  const { data: quote } = useQuery<QuotePreview>({
    queryKey: ['booking-late-quote', late.tail_booking_id, returnIso],
    queryFn: () =>
      api.post<QuotePreview>('/pricing/quote', {
        car_id: late.car_id,
        pickup_at: late.pickup_at,
        return_at: returnIso,
      }),
    enabled: Boolean(returnIso),
  });
  const priced = quote?.outcome === 'priced' ? (quote.total_minor ?? 0) : null;
  const hours = returnIso
    ? Math.round((new Date(returnIso).getTime() - new Date(late.pickup_at).getTime()) / 36e5)
    : 0;
  const amountMinor = amount.trim() ? Math.round(Number(amount) * 100) : Number.NaN;

  const record = useSaveMutation<
    Record<string, unknown>,
    { booking: { id?: string; _id?: string } }
  >({
    resource: 'booking',
    mutationFn: (body) =>
      api.post<{ booking: { id?: string; _id?: string } }>(
        `/bookings/${late.tail_booking_id}/late-extension`,
        body,
      ),
    onSaved: (res) => {
      setError('');
      navigate(`/bookings/${res.booking.id ?? res.booking._id}`);
    },
    onError: (err: unknown) => {
      const clash = err instanceof ApiError ? clashSentence(err.details.clash) : null;
      setError(
        clash ??
          (err instanceof ApiError || err instanceof Error
            ? err.message
            : 'The late extension could not be recorded.'),
      );
    },
  });

  function onRecord() {
    if (!returnIso) {
      setError('Choose the new return time. Nothing was saved.');
      return;
    }
    if (kind === 'outside' && (!(amountMinor > 0) || !reference.trim())) {
      setError('Enter the amount paid and a reference. Nothing was saved.');
      return;
    }
    void record.save({
      return_at: returnIso,
      ...(quote?.pricing_template_id ? { pricing_template_id: quote.pricing_template_id } : {}),
      payment:
        kind === 'outside'
          ? { kind, amount_minor: amountMinor, method, reference: reference.trim() }
          : { kind },
    });
  }

  const ended = late.tail_status !== 'checked_out';
  return (
    <div data-testid="late-extension-form" className="flex flex-col gap-4">
      <h3 className="text-sm font-semibold text-ink">Extend rental (late)</h3>
      <p className="text-sm text-steel">
        The return time passed {when(late.pickup_at)}{' '}
        {ended
          ? 'and the rental has ended. A late extension is added as an ended leg on the same car.'
          : 'and the car is still checked out.'}
      </p>
      <FormField
        label="New return time"
        name="late-return-at"
        hint={`Must be after ${when(late.pickup_at)}. The car must be free until then.`}
      >
        <Input
          id="late-return-at"
          type="datetime-local"
          value={returnAt}
          onChange={(e) => setReturnAt(e.target.value)}
        />
      </FormField>
      {priced !== null && (
        <p className="text-sm text-ink">
          Price for the extra {hours} {hours === 1 ? 'hour' : 'hours'} by the rate card:{' '}
          <span data-testid="late-extension-price" className="font-semibold">
            {rm(priced)}
          </span>
        </p>
      )}
      <fieldset className="flex flex-col gap-2">
        <legend className="text-sm font-medium text-ink mb-1">Payment</legend>
        <label className="flex items-center gap-2 text-sm">
          <input
            type="radio"
            name="late-payment"
            checked={kind === 'outside'}
            onChange={() => setKind('outside')}
          />
          Already paid outside the app
        </label>
        <label className="flex items-center gap-2 text-sm">
          <input
            type="radio"
            name="late-payment"
            checked={kind === 'qr'}
            onChange={() => setKind('qr')}
          />
          Customer pays by CARii's QR
        </label>
      </fieldset>
      {kind === 'outside' ? (
        <div className="flex flex-col gap-4">
          <FormField
            label="Amount paid (RM)"
            name="late-amount"
            hint="What the customer actually paid. It is recorded as the price of this extension."
          >
            <Input
              id="late-amount"
              inputMode="decimal"
              value={amount}
              onChange={(e) => setAmount(e.target.value)}
            />
          </FormField>
          <FormField label="Method" name="late-method" hint="Cash, bank transfer, QR or other.">
            <Select id="late-method" value={method} onChange={(e) => setMethod(e.target.value)}>
              {METHODS.map(([v, label]) => (
                <option key={v} value={v}>
                  {label}
                </option>
              ))}
            </Select>
          </FormField>
          <FormField
            label="Reference"
            name="late-reference"
            hint="Required. A receipt number, transfer reference or note."
          >
            <Input
              id="late-reference"
              maxLength={120}
              value={reference}
              onChange={(e) => setReference(e.target.value)}
            />
          </FormField>
          {priced !== null && amountMinor > 0 && amountMinor !== priced && (
            <p data-testid="late-extension-differs" className="text-sm text-semantic-warning-text">
              The amount differs from the rate card ({rm(priced)}). {rm(amountMinor)} will be
              recorded.
            </p>
          )}
        </div>
      ) : (
        <p data-testid="late-extension-qr-note" className="text-sm text-steel">
          The extension waits for payment at {priced !== null ? rm(priced) : 'the rate card price'}.
          The customer is told, pays with CARii's QR (the penalty QR) in their banking app and
          uploads the receipt; you approve it here. It is never cancelled automatically: the
          customer still owes it.
        </p>
      )}
      {error && (
        <p data-testid="late-extension-error" className="text-sm text-semantic-danger-text">
          {error}
        </p>
      )}
      <div className="flex gap-2">
        <Button variant="secondary" onClick={onCancel}>
          Cancel
        </Button>
        <Button onClick={onRecord} disabled={record.isPending}>
          {kind === 'outside' ? 'Record late extension' : 'Create and tell the customer'}
        </Button>
      </div>
    </div>
  );
}
